import { useState, useMemo } from "react";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useMyTasks } from "@/hooks/use-my-tasks";
import { useTimebankTransactions } from "@/hooks/use-timebank";
import { cn } from "@/lib/utils";
import { SrIcon } from "@/components/ui/SrIcon";
import RequestDepositDialog from "./RequestDepositDialog";

interface TimebankEntryButtonProps {
  timeEntryId: string;
  budgetLineId: string | null;
  taskDescription: string;
  entryHours: number;
  /** Compact mode for desktop table cell */
  compact?: boolean;
}

/**
 * Knapp som vises på en timeføring, slik at brukeren kan sende (deler av)
 * timene til timebanken. VIB-962: ALLE timeføringer kan bankes, også interne
 * prosjekter, reisetid og linjer uten budget_line/assignment. assignment_id
 * kobles på når vi finner en match, men er valgfritt (nullable i DB).
 */
export default function TimebankEntryButton({
  timeEntryId,
  budgetLineId,
  taskDescription,
  entryHours,
  compact = false,
}: TimebankEntryButtonProps) {
  const { activeUser } = useImpersonation();
  const userId = activeUser?.id ?? null;
  const { data: myTasksData } = useMyTasks(userId);
  const { data: transactions = [] } = useTimebankTransactions(userId);
  const [open, setOpen] = useState(false);

  // Slå opp assignment_id fra budget_line_id (per innlogget bruker).
  const assignmentId = useMemo(() => {
    if (!budgetLineId) return null;
    const match = (myTasksData?.tasks ?? []).find((t) => t.budget_line_id === budgetLineId);
    return match?.id ?? null;
  }, [budgetLineId, myTasksData]);

  // Allerede banket fra denne timeføringen (pending + approved).
  const { alreadyBanked, hasApproved, hasPending } = useMemo(() => {
    let alreadyBanked = 0;
    let hasApproved = false;
    let hasPending = false;
    for (const t of transactions) {
      if (t.kind !== "deposit" || t.time_entry_id !== timeEntryId) continue;
      if (t.status === "approved") {
        alreadyBanked += Number(t.hours);
        hasApproved = true;
      } else if (t.status === "pending") {
        alreadyBanked += Number(t.hours);
        hasPending = true;
      }
    }
    return { alreadyBanked, hasApproved, hasPending };
  }, [transactions, timeEntryId]);

  // VIB-962: vis knappen for ALLE timeføringer med faktiske timer. Tidligere
  // krevde vi budget_line + assignment, men det skjulte interne prosjekter,
  // reisetid og ukoblede linjer. assignment_id er valgfri ved deposit.
  if (!userId || entryHours <= 0) return null;

  const remaining = Math.max(0, entryHours - alreadyBanked);
  const fullyBanked = remaining < 0.0001 && (hasApproved || hasPending);

  if (fullyBanked) {
    const isApprovedOnly = hasApproved && !hasPending;
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1 text-[10px] whitespace-nowrap",
          isApprovedOnly ? "text-success" : "text-yellow-700",
        )}
        title={isApprovedOnly ? "Sendt til timebank og godkjent" : "Søknad venter godkjenning"}
      >
        <SrIcon name={isApprovedOnly ? "check" : "clock"} className="w-3 h-3 shrink-0" />
        <span>{isApprovedOnly ? "I timebank" : "Venter"}</span>
      </span>
    );
  }

  const partial = alreadyBanked > 0;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={cn(
          "inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap",
          compact ? "px-1.5 py-0.5 rounded hover:bg-muted" : "h-9 px-3 rounded-lg border border-border bg-card",
        )}
        title={
          partial
            ? `${alreadyBanked.toFixed(1)} t allerede sendt — ${remaining.toFixed(1)} t igjen`
            : "Send timer til timebank"
        }
      >
        <SrIcon name="clock" className="w-3 h-3 shrink-0" />
        <span>{compact ? "Til bank" : "Send til timebank"}</span>
        {partial && <span className="text-[10px] text-yellow-700">({remaining.toFixed(1)} t)</span>}
      </button>
      <RequestDepositDialog
        open={open}
        onOpenChange={setOpen}
        userId={userId}
        timeEntryId={timeEntryId}
        assignmentId={assignmentId}
        taskDescription={taskDescription}
        entryHours={entryHours}
        alreadyBanked={alreadyBanked}
      />
    </>
  );
}
